import { AppCacheProvider } from "@mui/material-nextjs/v15-pagesRouter";
import { LicenseInfo } from "@mui/x-license";
import { PitstopApp } from "core/components/PitstopApp";
import { PublicApp } from "core/components/PublicApp";
import type { AppProps } from "next/app";
import { useState } from "react";
import "i18n.config";
import { ApplicationConfigurationErrorPage } from "core/pages/ApplicationConfigurationErrorPage";
import { exhaustiveCheck } from "core/utils/exhaustiveCheck";
import {
  type ApplicationMainProps,
  getApplicationInitialProps,
} from "../core/getApplicationInitialProps";

const MUI_LICENSE_KEY = process.env.NEXT_PUBLIC_MUI_X_LICENSE!;
if (!MUI_LICENSE_KEY) {
  throw new Error("MUI license key is not set");
}
LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);

export default function ApplicationMain(
  props: AppProps & ApplicationMainProps
) {
  const { Component, pageProps } = props;
  const [initialProps] = useState(props);
  if (initialProps.executionContext !== "server") {
    console.error(initialProps);
    throw new Error("Unexpected execution context: expected 'server'");
  }
  if (initialProps.ok === false) {
    return (
      <AppCacheProvider {...props}>
        <ApplicationConfigurationErrorPage
          message={initialProps.error.message}
          supportEmail="support@fixnetwork.com"
        />
      </AppCacheProvider>
    );
  }
  switch (initialProps.applicationContext.applicationId) {
    case "pitstop":
      return (
        <AppCacheProvider {...props}>
          <PitstopApp
            Component={Component}
            pageProps={pageProps}
            applicationContext={initialProps.applicationContext}
          />
        </AppCacheProvider>
      );
    case "public":
      return (
        <AppCacheProvider {...props}>
          <PublicApp
            Component={Component}
            pageProps={pageProps}
            applicationContext={initialProps.applicationContext}
          />
        </AppCacheProvider>
      );
    default:
      exhaustiveCheck(initialProps.applicationContext);
      throw new Error(
        `Unexpected applicationId: ${(initialProps.applicationContext as { applicationId: string }).applicationId}`
      );
  }
}
ApplicationMain.getInitialProps = getApplicationInitialProps;
